import React, { ReactNode } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: UserRole[];
  fallback?: ReactNode;
}

/**
 * RoleGuard - Conditionally renders children based on user role
 * Useful for showing/hiding UI elements based on permissions
 *
 * @example
 * <RoleGuard allowedRoles={['owner', 'advisor']}>
 *   <Button>Admin Only Action</Button>
 * </RoleGuard>
 */
const RoleGuard: React.FC<RoleGuardProps> = ({ children, allowedRoles, fallback = null }) => {
  const { user } = useAuth();

  if (!user) {
    return <>{fallback}</>;
  }

  if (!allowedRoles.includes(user.role)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default RoleGuard;

/**
 * Hook to check if current user has a specific role
 */
export function useHasRole(roles: UserRole | UserRole[]): boolean {
  const { user } = useAuth();

  if (!user) return false;

  const roleArray = Array.isArray(roles) ? roles : [roles];
  return roleArray.includes(user.role);
}

/**
 * Hook to check if current user is an owner
 */
export function useIsOwner(): boolean {
  return useHasRole('owner');
}

/**
 * Hook to check if current user is staff (owner, advisor, or technician)
 */
export function useIsStaff(): boolean {
  return useHasRole(['owner', 'advisor', 'technician']);
}

/**
 * Hook to check if current user is a customer
 */
export function useIsCustomer(): boolean {
  return useHasRole('customer');
}

/**
 * Hook to check if current user has a specific capability/permission.
 * Owners are always implicitly allowed. Other roles must have the
 * capability present in their profile.permissions array.
 */
export function useCapability(cap: string): boolean {
  const { user, profile } = useAuth();

  if (!user) return false;
  if (user.role === 'owner') return true;
  return Array.isArray(profile?.permissions) && profile!.permissions!.includes(cap);
}
